import { useEffect, useMemo, useState } from 'react';
import { t } from 'i18next';
import { jwtDecode } from 'jwt-decode';
import { AxiosInstance } from 'axios';

import { OnboardingHeader } from '../../../../components/OnboardingHeader';
import { Stepper } from '../../../../components/Stepper';
import { PageHeadline } from '../../../../components/atoms';
import { MainCustomButton, SecondaryCustomButton } from '../../../../components/Buttons';
import { CardInfo } from '../../components/CardInfo';

import { DialogCheckoutIsSecure } from '../../components/DialogCheckoutIsSecure';

import { useAuth } from '../../../../store/auth';
import { RootState } from '../../../../store';

import { useAppSelector } from '../../../../hooks/hooksRedux';
import { CompanyRegisteringService } from '../../../../services/CompanyRegisteringService';
import { AccountService } from '../../../../services/AccountService';
import { useApi } from '../../../../services/hooks/apiHook';
import { defaultColors } from '../../../../styles/style-guide';
import { useTranslation } from 'react-i18next';
import { randomInt } from '../../../../utils/random';
import { useStepperContext } from '../../../../components/Stepper/Context';
import { clearTransferStepSession } from '../constants';

export type GetSummaryCompanyResponse = {
  data: {
    id: number;
    slug: string;
    description: string;
    tooltip: string;
    value: number;
    is_unique: number;
    jurisdiction_id: number;
    is_active: string;
    created_at: Date;
    updated_at: Date;
    deleted_at: Date | null;
    type: string;
  };
};

export type JwtPayload = {
  company_id: number;
  exp: number;
  iat: number;
  iss: string;
  jti: string;
  nbf: number;
  prv: string;
  sub: string;
};

export const SummaryPage = () => {
  const [summary, setSummary] = useState<any>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);
  const stepperContext = useStepperContext();

  const { token } = useAuth();
  const { t } = useTranslation();
  const { legalPersonalitySlug } = useAppSelector((state: RootState) => state.language);
  const { colors } = useAppSelector((state: RootState) => state.styleGuide.data);

  const primaryColor = colors.primary || defaultColors.primary;
  const secondaryColor = colors.secondary || defaultColors.secondary;
  const api: AxiosInstance = useApi();
  const randomImage = useMemo(() => randomInt(1, 5), []);

  const headers = { 'Content-Language': legalPersonalitySlug, Authorization: `Bearer ${token}` };

  const generateCheckout = async () => {
    if (token) {
      const decodedAccessToken = jwtDecode<JwtPayload>(token);
      try {
        setLoading(true);
        setOpen(false);
        const response = await CompanyRegisteringService.registerCompanyTransfersPayments(
          api,
          undefined,
          decodedAccessToken.company_id,
          { headers }
        );
        if (response.status === 200) {
          setTimeout(() => {
            setLoading(false);
            clearTransferStepSession();
            window.open(response.data.payment_url, '_blank');
          }, 2000);
        }
      } catch (e: any) {
        setLoading(false);
        console.error('Error fetch in registerCompanyTransfersPayments', e);
      }
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await CompanyRegisteringService.getAccountPlanCompany(api, { headers });
        if (data) await getAddonsForCompanyTrasnfer(data.slug);
      } catch (error) {
        console.log('Fetch error in Get Account Plan Company', error);
      }
    })();
  }, [legalPersonalitySlug]);

  const getAddonsForCompanyTrasnfer = async (slugPlan: string) => {
    if (!slugPlan) throw new Error('Dont exist slugPlan');
    try {
      const { data } = await AccountService.getSummary(api, { slug_plan: slugPlan }, { headers });
      if (data) setSummary(data);
    } catch (error) {
      console.log('Fetch error in Get Summary For Company', error);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-50 lg:flex">
      <div className="w-full box-border lg:pr-[20vw]">
        <div className="px-6 py-8 lg:px-12">
          <div className="mx-auto w-full max-w-5xl">
            <OnboardingHeader showLogoutButton />

            <div className="mt-6">
              <PageHeadline
                title={t('titles.ttlSummary')}
                className="mt-8 shadow-xl"
                gradientFrom="from-primary"
                gradientTo="to-blue-800"
              />
            </div>

            <div className="mt-6">
              <Stepper currentStep={5} maxSteps={5} />
            </div>

            <div className="mt-8 flex flex-col items-center gap-4">
              <div className="w-full max-w-sm">
                <h5 style={{ color: secondaryColor }} className="mb-2 text-sm font-bold">
                  {t('titles.ttlOffshorePackage')}
                </h5>
                <CardInfo
                  primaryColor={primaryColor}
                  jurisdictionLabel={summary?.jurisdiction?.description}
                  data={[...(summary?.fees ?? []), ...(summary?.addons ?? [])]}
                />
              </div>
            </div>

            <div className="mx-auto mt-6 flex w-full max-w-sm flex-col gap-3">
              <MainCustomButton
                className="p-3"
                text={t('buttons.btnContinueToCheckout')}
                onClick={() => setOpen(true)}
                loading={loading}
                style={{ maxWidth: '100%', whiteSpace: 'nowrap' }}
              />
              <SecondaryCustomButton
                text={t('buttons.btnBack')}
                onClick={() => stepperContext.prevStep()}
                style={{ maxWidth: '100%', whiteSpace: 'nowrap' }}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="fixed right-0 top-0 hidden h-screen lg:block lg:w-1/5">
        <div className="relative h-full">
          <img src={`/img/image0${randomImage}.png`} className="h-full w-full object-cover" alt="" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-black/10 to-transparent" />
          <p className="absolute bottom-[25%] left-6 right-6 text-[32px] font-bold leading-tight text-white">
            {t('labels.lblWeveHelped')}
          </p>
        </div>
      </div>

      <DialogCheckoutIsSecure open={open} setOpen={setOpen} onOK={generateCheckout} />
    </div>
  );
};
